import React from 'react';

import { Link } from 'react-router-dom';

function AccountabilityBoundariesPacketTemplate() {
  return (
    <>

      <div className="max-w-4xl mx-auto py-12 px-6">
        <h1 className="text-3xl font-bold text-[#102A43] mb-8 border-b border-gray-200 pb-4">Accountability Boundaries Packet - Reference Template</h1>

        <div className="space-y-12">
          
          {/* SECTION 1: Legal Positioning Disclaimer */}
          <section className="bg-yellow-50 border-l-4 border-yellow-500 p-6">
            <h2 className="text-sm font-bold text-yellow-800 uppercase tracking-wide mb-2">Legal Disclaimer</h2>
            <p className="text-yellow-900 font-medium text-sm leading-relaxed">
              DISCLAIMER: This reference template is provided for illustrative purposes only. AI Accountability Standards Council does not review, edit, co-author, or approve the contents of organization documentation. Declaration verifies the presence of documented boundaries and controls, not the quality, safety, or outcomes of any system.
            </p>
          </section>

          {/* SECTION 2: Page Purpose */}
          <section>
            <h2 className="text-xl font-bold text-[#102A43] mb-4 uppercase tracking-wide">Page Purpose</h2>
            <p className="text-gray-800 leading-relaxed">
              This reference template is provided as an example of acceptable structure for documenting accountability boundaries. Use of this template is optional. Declaration requires equivalent documentation, not this specific document. AASC does not author, implement, or operate organization systems.
            </p>
          </section>

          {/* SECTION 3: Template Introduction */}
          <section>
            <h2 className="text-xl font-bold text-[#102A43] mb-4 uppercase tracking-wide">Template Introduction</h2>
            <p className="text-gray-800 leading-relaxed">
              This template demonstrates one acceptable approach to documenting accountability boundaries as required by Standard <Link to="/standards/ai-abnd-1-0" className="text-[#2F80ED] font-bold hover:underline">AI-ABND-1.0</Link>. Organizations may use, adapt, or create their own documentation. Key requirement is documenting what the system will/won't do, who is responsible for what, and what clients understand about system scope and limitations.
            </p>
          </section>

          {/* SECTION 4: Reference Template Content */}
          <section className="border border-[#E2E8F0] rounded-sm overflow-hidden shadow-sm">
            <div className="bg-gray-100 px-6 py-4 border-b border-[#E2E8F0]">
              <h2 className="text-lg font-bold text-gray-800 uppercase tracking-wide">Reference Template Content</h2>
            </div>
            
            <div className="p-8 space-y-10 bg-white">
              
              {/* Template Header */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b-2 border-gray-900 pb-6">
                <div>
                  <div className="text-xs text-gray-500 uppercase font-bold tracking-wider">Document Type</div>
                  <div className="font-bold text-xl text-[#102A43]">Accountability Boundaries Packet</div>
                </div>
                <div className="md:text-right space-y-1 text-sm text-[#64748B]">
                  <div><span className="font-bold text-[#102A43]">System Name:</span> [System Name]</div>
                  <div><span className="font-bold text-[#102A43]">Organization:</span> [Agency Name]</div>
                  <div><span className="font-bold text-[#102A43]">Effective Date:</span> [YYYY-MM-DD]</div>
                  <div><span className="font-bold text-[#102A43]">Version:</span> [1.0]</div>
                  <div><span className="font-bold text-[#102A43]">Last Updated:</span> [YYYY-MM-DD]</div>
                </div>
              </div>

              {/* TEMPLATE SECTION 1 */}
              <div>
                <h3 className="text-lg font-bold text-[#102A43] mb-4 border-b border-gray-200 pb-2">1. System Scope and Prohibitions</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">1.1 Purpose</h4>
                    <div className="bg-[#F9FAFB] p-3 text-sm italic text-gray-600 border border-gray-200 rounded-sm">
                      [Describe the primary function of the AI system. Example: "To process level 1 customer support tickets for e-commerce returns."]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">1.2 In-Scope Actions</h4>
                    <div className="bg-[#F9FAFB] p-3 text-sm italic text-gray-600 border border-gray-200 rounded-sm">
                      [List authorized actions. Example: "Reading ticket content, drafting responses based on knowledge base, tagging ticket priority."]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">1.3 Out-of-Scope Actions (Prohibitions)</h4>
                    <div className="bg-[#F9FAFB] p-3 text-sm italic text-gray-600 border border-gray-200 rounded-sm">
                      [List prohibited actions. Example: "Processing refunds, accessing credit card full numbers, banning users."]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">1.4 Escalation Triggers</h4>
                    <div className="bg-[#F9FAFB] p-3 text-sm italic text-gray-600 border border-gray-200 rounded-sm">
                      [List conditions requiring human handover. Example: "Sentiment score below threshold, explicit request for human agent, mention of legal threats."]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">1.5 Human Authority Points</h4>
                    <div className="bg-[#F9FAFB] p-3 text-sm italic text-gray-600 border border-gray-200 rounded-sm">
                      [Describe requirements for human approval. Example: "All refunds over $50 require human approval. All responses flagged as 'toxic' require human review."]
                    </div>
                  </div>

                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">1.6 Logging and Evidence</h4>
                    <div className="bg-[#F9FAFB] p-3 text-sm italic text-gray-600 border border-gray-200 rounded-sm">
                      [Describe how activity is logged. Example: "All conversations logged to CRM. Escalation events tagged 'ESCALATION' in logs."]
                    </div>
                  </div>

                   <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">1.7 Effective Date and Version</h4>
                    <div className="bg-[#F9FAFB] p-3 text-sm italic text-gray-600 border border-gray-200 rounded-sm">
                      [Date this document becomes effective. Version number.]
                    </div>
                  </div>
                </div>
              </div>

              {/* TEMPLATE SECTION 2 */}
              <div>
                <h3 className="text-lg font-bold text-[#102A43] mb-4 border-b border-gray-200 pb-2">2. Responsibility Domains and Boundaries</h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="border border-gray-200 p-4 rounded-sm bg-[#F9FAFB]">
                     <h4 className="font-bold text-[#102A43] mb-2 uppercase text-xs tracking-wider border-b border-gray-200 pb-1">Agency Domain</h4>
                     <ul className="list-disc pl-4 text-sm space-y-2 text-[#64748B] mt-2">
                       <li><strong>Owner Role:</strong> [Agency Name]</li>
                       <li><strong>Responsibilities:</strong> Configuration of prompts, monitoring of drift, maintenance of knowledge base logic.</li>
                       <li><strong>Boundaries:</strong> Agency is not responsible for errors arising from incorrect source data provided by client.</li>
                       <li><strong>Dependencies:</strong> Reliance on [Platform Name] API availability.</li>
                     </ul>
                  </div>
                  <div className="border border-gray-200 p-4 rounded-sm bg-[#F9FAFB]">
                     <h4 className="font-bold text-[#102A43] mb-2 uppercase text-xs tracking-wider border-b border-gray-200 pb-1">Client Domain</h4>
                     <ul className="list-disc pl-4 text-sm space-y-2 text-[#64748B] mt-2">
                       <li><strong>Owner Role:</strong> [Client Name]</li>
                       <li><strong>Responsibilities:</strong> Accuracy of provided knowledge base, final approval of published content (if applicable), defined business rules.</li>
                       <li><strong>Boundaries:</strong> Client accepts liability for actions taken by agent within approved scope.</li>
                       <li><strong>Client Decisions:</strong> Defining refund policies, approval of brand voice guidelines.</li>
                     </ul>
                  </div>
                </div>

                <div className="border border-gray-200 p-4 rounded-sm bg-[#F9FAFB]">
                   <h4 className="font-bold text-[#102A43] mb-2 uppercase text-xs tracking-wider border-b border-gray-200 pb-1">Platform and Third Party Domain</h4>
                   <ul className="list-disc pl-4 text-sm space-y-2 text-[#64748B] mt-2">
                     <li><strong>Platform Names:</strong> [e.g., OpenAI, Anthropic, Zendesk]</li>
                     <li><strong>Responsibilities:</strong> Hosting of foundational models, API uptime.</li>
                     <li><strong>Limitations:</strong> Subject to provider outages and model behavior changes.</li>
                     <li><strong>Shared Responsibility:</strong> Data security in transit (Platform) vs. Data classification (Agency/Client).</li>
                   </ul>
                </div>
              </div>

              {/* TEMPLATE SECTION 3 */}
              <div>
                <h3 className="text-lg font-bold text-[#102A43] mb-4 border-b border-gray-200 pb-2">3. Client Acknowledgment</h3>
                <div className="bg-[#F9FAFB] p-6 border border-gray-200 rounded-sm text-sm space-y-6">
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">Acknowledgment Statement</h4>
                    <p className="italic text-gray-600">
                      "By signing below, the Client acknowledges they have reviewed the Accountability Boundaries defined above. The Client understands the authorized scope of the AI system, the specific prohibitions in place, and the division of responsibilities."
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-800 text-sm uppercase mb-2">Additional Acknowledgments</h4>
                    <ul className="list-disc pl-5 space-y-1 text-gray-600 italic">
                      <li>Client understands that AI systems may produce variable outputs.</li>
                      <li>Client confirms that provided knowledge base data is accurate.</li>
                    </ul>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pt-6">
                    <div>
                      <div className="border-b border-gray-400 mb-2 h-8"></div>
                      <div className="text-xs uppercase font-bold text-gray-500">Client Signature</div>
                    </div>
                     <div>
                      <div className="border-b border-gray-400 mb-2 h-8"></div>
                      <div className="text-xs uppercase font-bold text-gray-500">Date</div>
                    </div>
                     <div>
                      <div className="border-b border-gray-400 mb-2 h-8"></div>
                      <div className="text-xs uppercase font-bold text-gray-500">Client Name (Printed)</div>
                    </div>
                     <div>
                      <div className="border-b border-gray-400 mb-2 h-8"></div>
                      <div className="text-xs uppercase font-bold text-gray-500">Client Title</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* TEMPLATE SECTION 4 */}
              <div>
                <h3 className="text-lg font-bold text-[#102A43] mb-4 border-b border-gray-200 pb-2">4. Declaration Relationship</h3>
                 <div className="bg-blue-50 p-4 border border-blue-100 rounded-sm text-sm text-[#2F80ED]">
                    <p className="font-bold mb-2">Note to Auditors/Reviewers:</p>
                    <p>
                      This document serves as evidence for AASC Accountability Declaration Requirement <strong>AI-ABND-1.0 (Boundary Documentation)</strong>.
                    </p>
                    <p className="mt-2 text-xs">
                      <em>Use of this specific template is optional. Equivalent documentation covering scope, prohibitions, and responsibility domains is acceptable.</em>
                    </p>
                 </div>
              </div>

            </div>
          </section>

          {/* SECTION 5: Guidance Notes */}
          <section>
            <h2 className="text-xl font-bold text-[#102A43] mb-4 uppercase tracking-wide">Guidance Notes</h2>
            <div className="grid md:grid-cols-2 gap-8 text-gray-800">
               <div>
                 <h3 className="font-bold text-[#102A43] mb-2">Using This Template</h3>
                 <ul className="list-disc pl-5 text-sm space-y-2">
                   <li><strong>Must Do:</strong> You must document the "in scope" and "out of scope" actions.</li>
                   <li><strong>Must Do:</strong> You must obtain client acknowledgment (signature or digital equivalent).</li>
                   <li><strong>May Do:</strong> You may adapt the formatting to match your agency branding.</li>
                   <li><strong>May Do:</strong> You may add additional legal clauses as advised by your counsel.</li>
                 </ul>
               </div>
               <div>
                 <h3 className="font-bold text-[#102A43] mb-2">Declaration Scope</h3>
                 <ul className="list-disc pl-5 text-sm space-y-2">
                   <li><strong>AASC Verifies:</strong> That this document exists for deployed agents.</li>
                   <li><strong>AASC Verifies:</strong> That boundaries are explicitly defined.</li>
                   <li><strong>AASC Does NOT:</strong> Validate the legal enforceability of your contract.</li>
                   <li><strong>AASC Does NOT:</strong> Audit the technical code implementation of these boundaries (unless specified in advanced audit scopes).</li>
                 </ul>
               </div>
            </div>
            <div className="mt-6 bg-gray-100 p-4 rounded-sm text-sm text-[#64748B] italic">
              <strong>Declaration Confirmation:</strong> Completing this document helps satisfy the evidence requirements for Accountability Declaration and supports the validity of a Accountability Declaration Declaration.
            </div>
          </section>

          {/* SECTION 6: Questions */}
          <section className="bg-[#F9FAFB] p-8 border-t border-gray-200">
            <h2 className="text-lg font-bold text-[#102A43] mb-3">Questions and Support</h2>
            <p className="text-[#64748B] text-sm mb-4 leading-relaxed">
              The AASC can provide clarification regarding standard requirements and template structure. We cannot provide legal advice, system design services, or specific implementation support.
            </p>
            <div className="flex flex-col md:flex-row gap-4 md:items-center">
              <a href="mailto:support@aiaccountabilitycouncil.com" className="inline-flex items-center text-sm font-bold text-[#2F80ED] hover:underline">
                Contact Standards Support
              </a>
              <span className="hidden md:inline text-gray-300">|</span>
              <Link to="/standards/ai-abnd-1-0" className="inline-flex items-center text-sm font-bold text-[#64748B] hover:text-[#102A43] hover:underline">
                Return to AI-ABND-1.0 Standard
              </Link>
            </div>
          </section>

        </div>
      </div>
    </>
  );
}

export default AccountabilityBoundariesPacketTemplate;